import {
    AdditionalFieldConfig,
    ColumnDisplayPreference,
    ConfigurableOption,
    FreshnessState,
    ILegalContact,
    Identity,
    LegalContact,
    WhosMyLawyerContent,
    WhosMyLawyerContentFactory
} from "@amzn/ask-legal-domain";
import {
    Box,
    CollectionPreferencesProps,
    Grid,
    Link,
    PropertyFilterProps,
    SpaceBetween,
    StatusIndicator,
    TableProps
} from "@amzn/awsui-components-react";
import { Builder } from "builder-pattern";
import * as React from "react";
import { DateTimeFormatter } from "../../utils/date-time-utils";
import { LegalContactConstants } from "../../utils/legal-contact.constant";

import "./../../styles/component/legal-contact/view-layout.scss";

export namespace LegalContactsPolarisFactory {
    export namespace Table {
        // Helper to get expanded primary value for rendering
        function getExpandedValue(row: LegalContact.Record, fieldKey: string) {
            const expandedRow = row as ExpandedLegalContactRow;
            if (expandedRow._primaryFieldKey !== fieldKey) return null;
            return expandedRow._expandedPrimaryValue;
        }

        // Helper to get first sorted option by displayValue
        function getFirstSortedOption(options: ConfigurableOption[] | undefined): ConfigurableOption | undefined {
            if (!options?.length) return undefined;
            return options.slice().sort((a, b) =>
                (a.displayValue?.toLowerCase() ?? "").localeCompare(b.displayValue?.toLowerCase() ?? "")
            )[0];
        }

        // Helper to get sorted concatenated string of all options for sorting
        function getSortedConcatenatedValue(options: ConfigurableOption[] | undefined): string {
            if (!options?.length) return "";
            return options
                .slice()
                .sort((a, b) =>
                    (a.displayValue?.toLowerCase() ?? "").localeCompare(b.displayValue?.toLowerCase() ?? "")
                )
                .map(opt => opt.displayValue?.toLowerCase() ?? "")
                .join("|");
        }

        export const LegalContactUser = Builder<TableProps.ColumnDefinition<ExpandedLegalContactRow>>()
            .id("legalContactUser")
            .header("Legal Contact")
            .cell(e => <LegalContactUserComp
                    user={e.legalContactUser}
                    jobTitle={e.legalContactJobTitle}
                    badgeColor={e.legalContactBadgeColor}
                />)
            .sortingField("legalContactUser.name")
            .sortingComparator((a, b) =>
                (a.legalContactUser?.name?.toLowerCase() ?? "").localeCompare(
                    b.legalContactUser?.name?.toLowerCase() ?? ""
                )
            )
            .minWidth("300px")
            .isRowHeader(true)
            .build();

        export const LegalTeams = (options: ConfigurableOption[]) =>  Builder<TableProps.ColumnDefinition<ExpandedLegalContactRow>>()
            .id("legalTeams")
            .header("Legal Team")
            .cell(e => {
                const expandedValue = getExpandedValue(e, "legalTeams");
                const firstSorted = getFirstSortedOption(e.legalTeamOptions);
                const optionList = expandedValue != null
                    ? (expandedValue ? [expandedValue as ConfigurableOption] : [])
                    : (firstSorted ? [firstSorted] : []);

                return <WrapCellComp>
                    <ConfigurableOptionsListComp
                        optionList={optionList?.map(option => options.find(o => o.id === option.id) || option)}
                    />
                </WrapCellComp>;
            })
            .sortingField("legalTeams")
            .sortingComparator((a, b) => {
                const expandedA = getExpandedValue(a, "legalTeams");
                const expandedB = getExpandedValue(b, "legalTeams");
                const valueA = expandedA != null
                    ? (expandedA as ConfigurableOption)?.displayValue?.toLowerCase() ?? ""
                    : getFirstSortedOption(a.legalTeamOptions)?.displayValue?.toLowerCase() ?? "";
                const valueB = expandedB != null
                    ? (expandedB as ConfigurableOption)?.displayValue?.toLowerCase() ?? ""
                    : getFirstSortedOption(b.legalTeamOptions)?.displayValue?.toLowerCase() ?? "";

                return valueA.localeCompare(valueB);
            })
            .minWidth("300px")
            .maxWidth("300px")
            .build();

        export const LegalSubTeams = (options: ConfigurableOption[]) =>  Builder<TableProps.ColumnDefinition<ExpandedLegalContactRow>>()
            .id("legalSubTeams")
            .header("Legal Sub-Team")
            .cell(e => {
                const expandedValue = getExpandedValue(e, "legalSubTeams");
                const optionList = expandedValue != null
                    ? (expandedValue ? [expandedValue as ConfigurableOption] : [])
                    : e.legalSubTeamOptions;

                return <WrapCellComp>
                    <ConfigurableOptionsListComp
                        optionList={optionList?.map(option => options.find(o => o.id === option.id) || option)}
                    />
                </WrapCellComp>;
            })
            .sortingField("legalSubTeams")
            .sortingComparator((a, b) => {
                const expandedA = getExpandedValue(a, "legalSubTeams");
                const expandedB = getExpandedValue(b, "legalSubTeams");
                const valueA = expandedA != null
                    ? (expandedA as ConfigurableOption)?.displayValue?.toLowerCase() ?? ""
                    : getSortedConcatenatedValue(a.legalSubTeamOptions);
                const valueB = expandedB != null
                    ? (expandedB as ConfigurableOption)?.displayValue?.toLowerCase() ?? ""
                    : getSortedConcatenatedValue(b.legalSubTeamOptions);

                return valueA.localeCompare(valueB);
            })
            .minWidth("300px")
            .maxWidth("300px")
            .build();

        export const Manager = Builder<TableProps.ColumnDefinition<ExpandedLegalContactRow>>()
            .id("manager")
            .header("Manager")
            .cell(e => <Box textAlign="center">
                <UserPhoneToolComp user={e.legalContactManager} />
            </Box>)
            .sortingField("manager")
            .sortingComparator((a, b) =>
                (a.legalContactManager?.name?.toLowerCase() ?? "").localeCompare(
                    b.legalContactManager?.name?.toLowerCase() ?? ""
                )
            )
            .minWidth("200px")
            .maxWidth("200px")
            .build();

        export const OfficeLocation = Builder<TableProps.ColumnDefinition<ExpandedLegalContactRow>>()
            .id("officeLocation")
            .header("Office Location")
            .cell(e => <WrapCellComp>
                {e.legalContactOfficeLocation}
            </WrapCellComp>)
            .sortingField("officeLocation")
            .sortingComparator((a, b) =>
                (a.legalContactOfficeLocation?.toLowerCase() ?? "").localeCompare(
                    b.legalContactOfficeLocation?.toLowerCase() ?? ""
                )
            )
            .minWidth("300px")
            .maxWidth("300px")
            .build();

        export const LegalScopes = (options: ConfigurableOption[]) =>  Builder<TableProps.ColumnDefinition<ExpandedLegalContactRow>>()
            .id("legalScopes")
            .header("Scope")
            .cell(e => {
                const expandedValue = getExpandedValue(e, "legalScopes");
                const optionList = expandedValue != null
                    ? (expandedValue ? [expandedValue as ConfigurableOption] : [])
                    : e.legalScopeOptions;

                return <WrapCellComp>
                    <ConfigurableOptionsListComp
                        optionList={optionList?.map(option => {
                            if (option.id.includes(WhosMyLawyerContentFactory.CONFIGURABLE_OPTION_ADHOC_PREFIX)) return option;
                            return options.find(o => o.id === option.id) || option;
                        })}
                    />
                </WrapCellComp>;
            })
            .sortingField("legalScopes")
            .sortingComparator((a, b) => {
                const expandedA = getExpandedValue(a, "legalScopes");
                const expandedB = getExpandedValue(b, "legalScopes");
                const valueA = expandedA != null
                    ? (expandedA as ConfigurableOption)?.displayValue?.toLowerCase() ?? ""
                    : getSortedConcatenatedValue(a.legalScopeOptions);
                const valueB = expandedB != null
                    ? (expandedB as ConfigurableOption)?.displayValue?.toLowerCase() ?? ""
                    : getSortedConcatenatedValue(b.legalScopeOptions);

                return valueA.localeCompare(valueB);
            })
            .minWidth("300px")
            .maxWidth("300px")
            .build();

        export const BusinessLeader = Builder<TableProps.ColumnDefinition<ExpandedLegalContactRow>>()
            .id("businessLeader")
            .header("Business Leader (Director or VP)")
            .cell(e => {
                const expandedValue = getExpandedValue(e, "businessLeader");
                const leaders = expandedValue != null
                    ? (expandedValue ? [expandedValue as Identity] : [])
                    : e.businessLeader;

                return <WrapCellComp>
                    <BusinessLeaderComp leaders={leaders} />
                </WrapCellComp>;
            })
            .sortingField("businessLeader")
            .sortingComparator((a, b) => {
                const valueA = getExpandedValue(a, "businessLeader") ?? a.businessLeader?.[0];
                const valueB = getExpandedValue(b, "businessLeader") ?? b.businessLeader?.[0];

                return ((valueA as Identity)?.name?.toLowerCase() ?? "").localeCompare(
                    (valueB as Identity)?.name?.toLowerCase() ?? ""
                );
            })
            .minWidth("200px")
            .maxWidth("200px")
            .build();

        export const FreshnessDate = Builder<TableProps.ColumnDefinition<LegalContact.Record>>()
            .id("freshnessDate")
            .header("Freshness Date")
            .cell(e => <>{DateTimeFormatter.formatDate(e.freshnessDate)}</>)
            .sortingField("freshnessDate")
            .sortingComparator((a, b) =>
                (a.freshnessDate?.getTime() ?? -Infinity) - (b.freshnessDate?.getTime() ?? -Infinity)
            )
            .build();

        export const LastModifiedDate = Builder<TableProps.ColumnDefinition<LegalContact.Record>>()
            .id("lastModifiedDate")
            .header("Last Modified Date")
            .cell(e => <>{DateTimeFormatter.formatDate(e.versionInfo?.lastModifiedTimestamp)}</>)
            .sortingField("lastModifiedDate")
            .sortingComparator((a, b) =>
                (a.versionInfo?.lastModifiedTimestamp?.getTime() ?? -Infinity) -
                (b.versionInfo?.lastModifiedTimestamp?.getTime() ?? -Infinity)
            )
            .build();

        export const LastModifiedBy = Builder<TableProps.ColumnDefinition<LegalContact.Record>>()
            .id("lastModifiedBy")
            .header("Last Modified By")
            .cell(e => {
                const id = e.versionInfo?.lastModifiedBy?.id;
                if (!id) return <>-</>;
                return <Link href={`https://phonetool.amazon.com/users/${id}`}>{id}@</Link>;
            })
            .sortingField("lastModifiedBy")
            .sortingComparator((a, b) =>
                (a.versionInfo?.lastModifiedBy?.id?.toLowerCase() ?? "").localeCompare(
                    b.versionInfo?.lastModifiedBy?.id?.toLowerCase() ?? ""
                )
            )
            .build();

        export const FreshnessStatus = Builder<TableProps.ColumnDefinition<ILegalContact.LoadAllLegalContactsByUserOutput>>()
            .id("freshnessDateStatus")
            .header("Freshness Date (Status)")
            .cell(e => <Box textAlign="center">
                <SpaceBetween size="s" direction="vertical">
                    {DateTimeFormatter.formatDate(e.freshnessDate)}
                    <StatusIndicator type={
                        (e.freshnessStatus === FreshnessState.EXPIRED) ? "error" : "success"
                    }>{e.freshnessStatus}</StatusIndicator>
                </SpaceBetween>
            </Box>)
            .sortingField("freshnessDate")
            .build();

        export function toColumnDefinition(
            whosMyLawyerContent?: WhosMyLawyerContent
        ) {
            const columnDefinitions = [
                LegalContactUser,
                LegalTeams(whosMyLawyerContent?.dataOptions?.teamOptions || []),
                LegalSubTeams(whosMyLawyerContent?.dataOptions?.subTeamOptions || []),
                LegalScopes(whosMyLawyerContent?.dataOptions?.scopeOptions || []),
                Manager,
                OfficeLocation,
                BusinessLeader
            ];

            // Additional field columns
            if (whosMyLawyerContent?.dataOptions?.additionalFieldsConfig) {
                whosMyLawyerContent.dataOptions.additionalFieldsConfig.forEach(config => {
                    const additionalColumn = Builder<TableProps.ColumnDefinition<ExpandedLegalContactRow>>()
                        .id(`${LegalContactConstants.ADDITIONAL_FIELD_PREFIX}${config.fieldKey}`)
                        .header(config.name)
                        .cell(e => <WrapCellComp>
                            {extractAdditionalFieldValue(e, config)}
                        </WrapCellComp>)
                        .sortingField(`${LegalContactConstants.ADDITIONAL_FIELD_PREFIX}${config.fieldKey}`)
                        .sortingComparator((a, b) => {
                            const getFieldValue = (record: ExpandedLegalContactRow) => {
                                const expanded = getExpandedValue(record, `${LegalContactConstants.ADDITIONAL_FIELD_PREFIX}${config.fieldKey}`);
                                const value = expanded != null ? expanded : record.additionalFields?.[config.fieldKey];
                                const firstValue = Array.isArray(value) ? value[0] : value;
                                const displayValue = (firstValue as ConfigurableOption)?.displayValue ?? firstValue;
                                return String(displayValue ?? "").toLowerCase();
                            };
                            return getFieldValue(a).localeCompare(getFieldValue(b));
                        })
                        .minWidth("200px")
                        .maxWidth("300px")
                        .build();

                    columnDefinitions.push(additionalColumn);
                });
            }

            return columnDefinitions;
        }

        function extractAdditionalFieldValue(record: LegalContact.Record, config: AdditionalFieldConfig) {
            const expandedValue = getExpandedValue(record, `${LegalContactConstants.ADDITIONAL_FIELD_PREFIX}${config.fieldKey}`);
            const fieldValue = expandedValue !== null
                ? expandedValue
                : record.additionalFields?.[config.fieldKey];

            if (!fieldValue) return <></>;

            if (config.type === "choice") {
                const optionList = Array.isArray(fieldValue) ? fieldValue : [fieldValue as ConfigurableOption];
                return <ConfigurableOptionsListComp optionList={optionList} />;
            } else if (config.type === "links") {
                return <Link href={fieldValue} external>{fieldValue}</Link>;
            }
            return <>{String(fieldValue)}</>;
        }

        export function toMyContactsColumnDefinition() {
            return [
                LegalContactUser,
                FreshnessStatus,
                LegalTeams([]),
                LegalSubTeams([]),
                LegalScopes([]),
                Manager,
                OfficeLocation,
                BusinessLeader
            ];
        }

        export function toAdminColumnDefinition() {
            return [
                LegalContactUser,
                LegalTeams([]),
                LegalSubTeams([]),
                LegalScopes([]),
                Manager,
                OfficeLocation,
                BusinessLeader,
                FreshnessDate,
                LastModifiedDate,
                LastModifiedBy,
            ];
        }

        export function getDefaultColumnDisplay(whosMyLawyerContent?: WhosMyLawyerContent): ColumnDisplayPreference[] {
            return toColumnDefinition(whosMyLawyerContent).map(x => ({ id: x.id, visible: true }));
        }

        export function getColumnDisplayOptions(whosMyLawyerContent?: WhosMyLawyerContent): CollectionPreferencesProps.ContentDisplayOption[] {
            return toColumnDefinition(whosMyLawyerContent).map(x => ({
                id: x.id,
                label: x.header,
                alwaysVisible: x.isRowHeader
            }));
        }

        export function getSortingColumnById(id: string, whosMyLawyerContent?: WhosMyLawyerContent): TableProps.SortingColumn<LegalContact.Record> {
            const column = toColumnDefinition(whosMyLawyerContent).find(x => x.id === id);
            if (column) {
                return { sortingField: column.sortingField };
            }
            return DEFAULT_SORTING_STATE.sortingColumn;
        }

        export function toDefaultTablePreferences() {
            return Builder<CollectionPreferencesProps.Preferences<LegalContact.Record>>()
                .pageSize(10)
                .build();
        }

        /**
         * Extended row interface for expanded legal contacts
         */
        export interface ExpandedLegalContactRow extends LegalContact.Record {
            _expandedPrimaryValue: ConfigurableOption | Identity | string | null;
            _primaryFieldKey: string;
            _originalContactRef: string;
            _uniqueRowId: string;
        }

        /**
         * Expands contacts into rows based on primary field values.
         * Each value in a multi-value field creates a separate row.
         */
        export function expandRowsByPrimaryField(
            contacts: LegalContact.Record[],
            primaryField: string
        ): ExpandedLegalContactRow[] {
            const expanded: ExpandedLegalContactRow[] = [];

            for (const contact of contacts) {
                const values = getPrimaryFieldValues(contact, primaryField);

                if (values.length === 0) {
                    expanded.push({
                        ...contact,
                        _expandedPrimaryValue: null,
                        _primaryFieldKey: primaryField,
                        _originalContactRef: contact.ref.entityRef.entityId,
                        _uniqueRowId: `${contact.ref.entityRef.entityId}_0`
                    });
                } else {
                    for (let i = 0; i < values.length; i++) {
                        expanded.push({
                            ...contact,
                            _expandedPrimaryValue: values[i],
                            _primaryFieldKey: primaryField,
                            _originalContactRef: contact.ref.entityRef.entityId,
                            _uniqueRowId: `${contact.ref.entityRef.entityId}_${i}`
                        });
                    }
                }
            }

            return expanded;
        }

        function getPrimaryFieldValues(
            contact: LegalContact.Record,
            fieldKey: string
        ): (ConfigurableOption | Identity | string)[] {
            switch (fieldKey) {
                case "legalContactUser":
                    return [contact.legalContactUser];
                case "legalScopes":
                    return contact.legalScopeOptions || [];
                case "legalTeams":
                    return contact.legalTeamOptions || [];
                case "legalSubTeams":
                    return contact.legalSubTeamOptions || [];
                case "manager":
                    return contact.legalContactManager ? [contact.legalContactManager] : [];
                case "officeLocation":
                    return contact.legalContactOfficeLocation ? [contact.legalContactOfficeLocation] : [];
                case "businessLeader":
                    return contact.businessLeader || [];
                default:
                    const actualFieldKey = fieldKey.startsWith(LegalContactConstants.ADDITIONAL_FIELD_PREFIX)
                        ? fieldKey.substring(LegalContactConstants.ADDITIONAL_FIELD_PREFIX.length)
                        : fieldKey;
                    const value = contact.additionalFields?.[actualFieldKey];
                    if (Array.isArray(value)) {
                        return value;
                    }
                    return value ? [value] : [];
            }
        }
    }

    export namespace Filters {
        /**
         * Matches item's key object that includes query term in its name, id fields
         */
        function matchNameOrId(query: string, item: LegalContact.Record, key: string) {
            return item[key]?.name.toLowerCase().includes(query.toLowerCase()) ||
                item[key]?.id.toLowerCase().includes(query.toLowerCase());
        }

        /**
         * Apply special attribute filters.
         * Supported attributes - contact, manager
         */
        export function applySpecialAttributeFilter(query: string, item: LegalContact.Record) {
            if (query.includes(":")) {
                const data = query.split(":");
                const value = data[1].trim();
                switch (data[0]) {
                    case "contact":
                        return matchNameOrId(value, item, "legalContactUser");
                    case "manager":
                        return matchNameOrId(value, item, "legalContactManager");
                }
            }
            return false;
        }

        export function applyDefaultFilter(query: string, item: LegalContact.Record, fields: string[]) {
            return fields.some(field => {
                switch (field) {
                    case "legalContactUser":
                    case "legalContactManager":
                        return matchNameOrId(query, item, field);
                    case "businessLeader":
                        return item[field]?.some(x => x.name.toLowerCase().includes(query.toLowerCase()) ||
                            x.id.toLowerCase().includes(query.toLowerCase()));
                    case "legalContactJobTitle":
                    case "legalContactOfficeLocation":
                        return item[field]?.toLowerCase().includes(query.toLowerCase());
                    case "legalScopeOptions":
                    case "legalTeamOptions":
                    case "legalSubTeamOptions":
                        return item[field]?.some(x => x.displayValue.toLowerCase().includes(query.toLowerCase()));
                    case "additionalFields":
                        if (!item.additionalFields) return false;
                        return Object.values(item.additionalFields).some(fieldValue => {
                            if (Array.isArray(fieldValue)) {
                                return fieldValue.some(option =>
                                    option.displayValue?.toLowerCase().includes(query.toLowerCase())
                                );
                            } else if (typeof fieldValue === "string") {
                                return fieldValue.toLowerCase().includes(query.toLowerCase());
                            }
                            return false;
                        });
                    default:
                        return JSON.stringify(item[field]).toLowerCase().includes(query.toLowerCase());
                }
            });
        }

        export function getDefaultFilterFields(): string[] {
            return [
                "legalContactUser",
                "legalContactJobTitle",
                "legalTeamOptions",
                "legalSubTeamOptions",
                "legalScopeOptions",
                "legalContactManager",
                "legalContactOfficeLocation",
                "businessLeader",
                "additionalFields"
            ];
        }

        export namespace PropertyFilters {
            export const LegalContactUserName = Builder<PropertyFilterProps.FilteringProperty>()
                .key("legalContactUser.name")
                .operators(["=", ":", "!=", "!:"])
                .propertyLabel("Legal Contact Name")
                .build();
            export const LegalContactUserAlias = Builder<PropertyFilterProps.FilteringProperty>()
                .key("legalContactUser.id")
                .operators(["=", ":", "!=", "!:"])
                .propertyLabel("Legal Contact Alias")
                .build();
            export const LegalScopeOptions = Builder<PropertyFilterProps.FilteringProperty>()
                .key("legalScopeOptions.displayValue")
                .operators([":", "!:"])
                .defaultOperator(":")
                .propertyLabel("Legal Scope")
                .build();
            export const LegalTeamOptions = Builder<PropertyFilterProps.FilteringProperty>()
                .key("legalTeamOptions.displayValue")
                .operators([":", "!:"])
                .defaultOperator(":")
                .propertyLabel("Legal Team")
                .build();
            export const LegalSubTeamOptions = Builder<PropertyFilterProps.FilteringProperty>()
                .key("legalSubTeamOptions.displayValue")
                .operators([":", "!:"])
                .defaultOperator(":")
                .propertyLabel("Legal Sub-Team")
                .build();
            export const BusinessLeaderName = Builder<PropertyFilterProps.FilteringProperty>()
                .key("businessLeader.name")
                .operators(["=", ":", "!=", "!:"])
                .propertyLabel("Business Leader Name")
                .build();
            export const BusinessLeaderAlias = Builder<PropertyFilterProps.FilteringProperty>()
                .key("businessLeader.id")
                .operators(["=", ":", "!=", "!:"])
                .propertyLabel("Business Leader Alias")
                .build();

            export function toDefaultFilteringProperties() {
                const filterColumns = [
                    LegalContactUserName,
                    LegalContactUserAlias,
                    LegalTeamOptions,
                    LegalSubTeamOptions,
                    LegalScopeOptions,
                    BusinessLeaderName,
                    BusinessLeaderAlias
                ];
                return filterColumns;
            }
            export function toMyContactsFilteringProperties() {
                return [
                    LegalTeamOptions,
                    LegalSubTeamOptions,
                    LegalScopeOptions,
                    BusinessLeaderName,
                    BusinessLeaderAlias
                ];
            }
        }
    }

    export const DEFAULT_SORTING_STATE: TableProps.SortingState<LegalContact.Record> = {
        sortingColumn: { sortingField: Filters.PropertyFilters.LegalContactUserName.key },
        isDescending: false
    };

    export const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences<LegalContact.Record> = {
        pageSize: 10,
        contentDisplay: Table.getDefaultColumnDisplay(),
        wrapLines: true,
        stripedRows: false,
        contentDensity: "compact",
        stickyColumns: { first: 1, last: 0 },
    };

    export const DEFAULT_PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizeOption[] = [
        { value: 5, label: "5 contacts" },
        { value: 10, label: "10 contacts" },
        { value: 25, label: "25 contacts" }
    ];

    const BadgeColor = LegalContact.BadgeDisplayColor;

    function getLegalContactClass(badgeColor: string): string | undefined {
        switch (badgeColor) {
            case BadgeColor.Blue:
            case BadgeColor.Orange:
            case BadgeColor.Red:
            case BadgeColor.Purple:
            case BadgeColor.Silver:
            case BadgeColor.Gold:
            case BadgeColor.Yellow:
            case BadgeColor.Green:
                return `legal-contact-user-${badgeColor.toLowerCase()}`;
            case "":
                return "legal-contact-user-gray";
            default:
                return undefined;
        }
    }

    function getBadgeTypeLabel(badgeColor: string): string {
        switch (badgeColor) {
            case BadgeColor.Blue: return "Full-Time Employee";
            case BadgeColor.Orange: return "Full-Time Employee (5+ years)";
            case BadgeColor.Red: return "Full-Time Employee (10+ years)";
            case BadgeColor.Purple: return "Full-Time Employee (15+ years)";
            case BadgeColor.Silver: return "Full-Time Employee (20+ years)";
            case BadgeColor.Gold: return "Full-Time Employee (25+ years)";
            case BadgeColor.Yellow: return "Contractor";
            case BadgeColor.Green: return "Contingent Worker";
            default: return "Badge color temporarily unavailable";
        }
    }

    const LegalContactUserComp = (props: {
        user: Identity;
        jobTitle?: string;
        badgeColor?: string;
    }) => {
        const badgeLabel = props.badgeColor !== undefined ? getBadgeTypeLabel(props.badgeColor) : undefined;

        return <Grid
            className={props.badgeColor !== undefined ? getLegalContactClass(props.badgeColor) : undefined}
            gridDefinition={[
                { colspan: 4 },
                { colspan: 8 }
            ]}
        >
            <img alt={`@${props.user.id} badge photo`} style={{
                height: "100px",
                width: "75px",
                paddingLeft: "2px"
            }} src={`https://badgephotos.corp.amazon.com/?uid=${props.user.id}`}
                aria-label={badgeLabel ? `${props.user.name ?? props.user.id} - ${badgeLabel}` : undefined}
                title={badgeLabel}
            />
            <Box textAlign="center">
                <br />
                <UserPhoneToolComp user={props.user} />
                {props.jobTitle && <>
                    <br />
                    {props.jobTitle}
                </>}
            </Box>
        </Grid>;
    };

    const UserPhoneToolComp = (props: {
        user: Identity;
    }) => {
        if (!props.user?.id) return <span>-</span>;
        return <Link href={`https://phonetool.amazon.com/users/${props.user.id}`}>
            {props.user.name}<br />(<em>{props.user.id}@</em>)
        </Link>;
    };

    const BusinessLeaderComp = (props: {
        leaders: Identity[];
    }) => {
        if (props.leaders.length === 0) return <>-</>;
        else return <SpaceBetween size="xs">
            {props.leaders.map(x => <Box key={x.id} textAlign="center"><UserPhoneToolComp user={x} /></Box>)}
        </SpaceBetween>;
    };

    const WrapCellComp = (props: {
        children: React.ReactNode;
    }) => {
        return <div className="cell-wrapped">
            {props.children}
        </div>;
    };

    const ConfigurableOptionsListComp = (props: {
        optionList: Partial<ConfigurableOption>[];
    }) => {
        if (!props.optionList?.length) return <>-</>;
        return <div className="string-list">
            {props.optionList?.map(x => <span className="string-item">{x.displayValue?.trim() || x.id}</span>)}
        </div>;
    };
}